import React, {useState} from "react";
import {motion} from "framer-motion";
import AnimatedLink from "../../../../../ui/motion/AnimatedLink";

interface TutoringComparisonSectionProps {
    kedPrice: number;
    persona: 'parent' | 'teen';
    ctaTarget?: string;
    includeParentBonus?: boolean;
    /**
     * Override the last bullet in the parent KED card (only when `includeParentBonus` is true).
     * Older webinars frame it as a bonus ("Bonus: kurs dla rodzica…"); for the 25-04 webinar we
     * frame ZN + the parent guide as bundled materials, not a bonus.
     */
    parentBonusItem?: string;
    installmentMode?: boolean;
}

const glassCard = {
    background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.35) 0%, rgba(26, 26, 46, 0.55) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
};

function formatNumber(num: number): string {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function sliderBackground(value: number, min: number, max: number): string {
    const pct = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #2ad167 0%, #2ad167 ${pct}%, rgba(255,255,255,0.12) ${pct}%, rgba(255,255,255,0.12) 100%)`;
}

const parentTutoringItems = [
    'Uczą tylko jednego przedmiotu',
    'Dziecko nadal nie wie, jak się uczyć samo',
    'Nadal wymaga przypominania i pilnowania',
    'Rachunek wraca co miesiąc',
    'Kończą się po maturze — problem zostaje'
];

const teenTutoringItems = [
    'Uczą tylko jednego przedmiotu',
    'Nadal nie wiesz, jak uczyć się samodzielnie',
    'Nadal potrzebujesz kogoś, kto Cię pilnuje',
    'Rachunek wraca co miesiąc',
    'Kończą się po maturze — problem zostaje'
];

const parentKedItemsBase = [
    'Działa na KAŻDY przedmiot — teraz i zawsze',
    'Dziecko samo siada do nauki — bez Ciebie',
    'Koniec z kłótniami i codziennym przypominaniem',
    'Dożywotni dostęp — nie wygasa po roku',
];

const teenKedItems = [
    'Działa na KAŻDY przedmiot — teraz i zawsze',
    'Sam/sama siadasz do nauki — bez zmuszania się',
    'Koniec z prokrastynacją i odkładaniem na później',
    'Dożywotni dostęp — nie wygasa po roku',
    'Społeczność i cotygodniowe spotkania z mentorem'
];

const parentNothingItems = [
    'Codzienne kłótnie przy kolacji — bez zmian',
    'Napięcie i frustracja w domu — trwa',
    'Dziecko marnuje swój potencjał — powoli',
    'Korepetycje i tak się pojawią — droższe',
    'Za rok: ten sam problem, ta sama bezsilność'
];

const teenNothingItems = [
    'Kolejne odkładanie nauki na później — bez zmian',
    'Frustracja i poczucie winy — trwa',
    'Marnujesz swój potencjał — powoli',
    'Korepetycje i tak się pojawią — droższe',
    'Za rok: ten sam problem, ta sama bezsilność'
];

export default function TutoringComparisonSection({
                                                      kedPrice,
                                                      persona,
                                                      ctaTarget = '#kup-teraz',
                                                      includeParentBonus = true,
                                                      parentBonusItem,
                                                      installmentMode = false
                                                  }: TutoringComparisonSectionProps) {
    const [subjects, setSubjects] = useState(2);
    const [hourlyRate, setHourlyRate] = useState(120);
    const [weeklyHours, setWeeklyHours] = useState(4);

    const monthlyTutoringCost = weeklyHours * hourlyRate * 4;
    const yearlyTutoringCost = monthlyTutoringCost * 12;

    const isParent = persona === 'parent';
    const parentKedItems = includeParentBonus
        ? [...parentKedItemsBase, parentBonusItem ?? 'Bonus: kurs dla rodzica (wart. 1.499 zł)']
        : [...parentKedItemsBase, 'Społeczność i cotygodniowe spotkania z mentorem'];

    function handleSubjectsChange(newSubjects: number) {
        const avgPerSubject = subjects > 0 ? weeklyHours / subjects : 2;
        setSubjects(newSubjects);
        setWeeklyHours(Math.min(15, Math.max(1, Math.round(newSubjects * avgPerSubject))));
    }

    return (
        <>
            <style>{`
                .ked-range-slider {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 100%;
                    height: 6px;
                    border-radius: 3px;
                    outline: none;
                    cursor: pointer;
                }
                .ked-range-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #2ad167;
                    cursor: pointer;
                    box-shadow: 0 0 8px rgba(42, 209, 103, 0.4);
                }
                .ked-range-slider::-moz-range-thumb {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #2ad167;
                    cursor: pointer;
                    border: none;
                    box-shadow: 0 0 8px rgba(42, 209, 103, 0.4);
                }
            `}</style>

            {/* Calculator heading */}
            <motion.div
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6}}
                className="text-center mt-16 mb-8"
            >
                <h2 className="fs-4 fw-bold text-white mb-3">Czy to drogo? Policz sam/a.</h2>
                <p className="fs-9 text-secondary-light mw-md-3xl mx-auto">Sprawdź, ile kosztuje Cię brak zmian.</p>
            </motion.div>

            {/* Calculator */}
            <motion.div
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.5}}
                className="p-4 p-md-6 rounded-4 mw-md-4xl mx-auto mb-8"
                style={{...glassCard, border: '1px solid rgba(255, 255, 255, 0.08)'}}
            >
                <h4 className="fs-7 fw-bold text-white mb-6 text-center">
                    <i className="bi bi-calculator me-2"></i>Ile wydajesz rocznie na korepetycje?
                </h4>

                <div className="d-flex flex-column gap-5 mb-6">
                    <div>
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-secondary-light fs-10">Liczba przedmiotów z korepetycjami</span>
                            <span className="text-white fw-bold fs-10">{subjects}</span>
                        </div>
                        <input
                            type="range"
                            className="ked-range-slider"
                            min={1} max={6} step={1}
                            value={subjects}
                            onChange={(e) => handleSubjectsChange(Number(e.target.value))}
                            style={{background: sliderBackground(subjects, 1, 6)}}
                        />
                    </div>
                    <div>
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-secondary-light fs-10">Cena za godzinę</span>
                            <span className="text-white fw-bold fs-10">{hourlyRate} zł</span>
                        </div>
                        <input
                            type="range"
                            className="ked-range-slider"
                            min={40} max={250} step={10}
                            value={hourlyRate}
                            onChange={(e) => setHourlyRate(Number(e.target.value))}
                            style={{background: sliderBackground(hourlyRate, 40, 250)}}
                        />
                    </div>
                    <div>
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-secondary-light fs-10">Godzin tygodniowo (łącznie)</span>
                            <span className="text-white fw-bold fs-10">{weeklyHours} h</span>
                        </div>
                        <input
                            type="range"
                            className="ked-range-slider"
                            min={1} max={15} step={1}
                            value={weeklyHours}
                            onChange={(e) => setWeeklyHours(Number(e.target.value))}
                            style={{background: sliderBackground(weeklyHours, 1, 15)}}
                        />
                    </div>
                </div>

                <div className="d-flex flex-wrap justify-content-center gap-3">
                    <div className="px-4 py-3 rounded-3 text-center" style={{
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        minWidth: '160px'
                    }}>
                        <span className="d-block fs-6 fw-bold text-danger">{formatNumber(monthlyTutoringCost)} zł</span>
                        <span className="text-white-50 fs-11">miesięcznie</span>
                    </div>
                    <div className="px-4 py-3 rounded-3 text-center" style={{
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        minWidth: '160px'
                    }}>
                        <span className="d-block fs-6 fw-bold text-danger">{formatNumber(yearlyTutoringCost)} zł</span>
                        <span className="text-white-50 fs-11">rocznie na korepetycje</span>
                    </div>
                    <div className="px-4 py-3 rounded-3 text-center" style={{
                        border: '1px solid rgba(42, 209, 103, 0.4)',
                        background: 'rgba(42, 209, 103, 0.12)',
                        minWidth: '160px'
                    }}>
                        <span
                            className="d-block fs-6 fw-bold text-success">{installmentMode ? `4 × ${formatNumber(kedPrice)} zł` : `${formatNumber(kedPrice)} zł`}</span>
                        <span
                            className="text-white-50 fs-11">{installmentMode ? 'KED 2.0 — wygodne raty' : 'KED 2.0 — raz, na zawsze'}</span>
                    </div>
                </div>
            </motion.div>

            {/* Comparison text */}
            <motion.p
                initial={{opacity: 0, y: 10}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.5}}
                className="fs-8 text-white mw-md-4xl mx-auto mb-10"
            >
                {isParent ? (
                    installmentMode
                        ? <>Wydajesz <b>{formatNumber(yearlyTutoringCost)} zł</b> rocznie i Twoje dziecko nadal nie wie,
                            jak
                            się uczyć samo.
                            KED 2.0 kosztuje <b>4 × {formatNumber(kedPrice)} zł</b> w wygodnych ratach.</>
                        : <>Wydajesz <b>{formatNumber(yearlyTutoringCost)} zł</b> rocznie i Twoje dziecko nadal nie wie,
                            jak
                            się
                            uczyć samo.
                            KED 2.0 kosztuje <b>{formatNumber(kedPrice)} zł raz</b> i zmienia to na zawsze.</>
                ) : (
                    installmentMode
                        ? <>Wydajesz <b>{formatNumber(yearlyTutoringCost)} zł</b> rocznie na korepetycje — i nadal nie
                            wiesz, jak się uczyć. KED 2.0 kosztuje <b>4 × {formatNumber(kedPrice)} zł</b> w wygodnych
                            ratach.</>
                        : <>Wydajesz <b>{formatNumber(yearlyTutoringCost)} zł</b> rocznie na korepetycje — i nadal nie
                            wiesz, jak się uczyć. KED 2.0 kosztuje <b>{formatNumber(kedPrice)} zł raz</b> i zmienia to na
                            zawsze.</>
                )}
            </motion.p>

            {/* 3 Comparison Cards */}
            <div className="row g-4 mw-md-5xl mx-auto mb-8">
                {/* Card 1: Korepetycje */}
                <div className="col-12 col-md-4">
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true}}
                        transition={{duration: 0.5}}
                        className="h-100 p-4 p-md-5 rounded-4 text-start"
                        style={{...glassCard, border: '1px solid rgba(239, 68, 68, 0.15)'}}
                    >
                        <i className="bi bi-cash-stack text-danger fs-4 d-block mb-3"></i>
                        <h4 className="fw-bold text-white fs-8 mb-1">Korepetycje rok po roku</h4>
                        <p className="text-danger fw-bold fs-5 mb-1">{formatNumber(yearlyTutoringCost)} zł</p>
                        <p className="text-white-50 fs-11 mb-4">/ rok · i tak dalej co rok</p>

                        <div className="d-flex flex-column gap-3 mb-4">
                            {(isParent ? parentTutoringItems : teenTutoringItems).map((item, i) => (
                                <div key={i} className="d-flex align-items-start gap-2">
                                    <i className="bi bi-x-lg text-danger fs-11 mt-1 flex-shrink-0"></i>
                                    <span className="text-white fs-11">{item}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto pt-3"
                             style={{borderLeft: '3px solid rgba(239, 68, 68, 0.3)', paddingLeft: '12px'}}>
                            <p className="text-white-50 fs-12 fst-italic mb-0">Leczysz objaw. Nie przyczynę. I
                                płacisz za to każdego miesiąca.</p>
                        </div>
                    </motion.div>
                </div>

                {/* Card 2: KED 2.0 (highlighted) */}
                <div className="col-12 col-md-4">
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true}}
                        transition={{duration: 0.5, delay: 0.1}}
                        className="h-100 p-4 p-md-5 rounded-4 text-start position-relative"
                        style={{...glassCard, border: '2px solid rgba(42, 209, 103, 0.4)'}}
                    >
                        <div className="position-absolute top-0 start-50 translate-middle">
                            <span className="badge bg-warning text-dark px-3 py-2 rounded-pill fs-12 fw-bold">NAJLEPSZA DECYZJA</span>
                        </div>

                        <i className="bi bi-rocket-takeoff text-success fs-4 d-block mb-3 mt-2"></i>
                        <h4 className="fw-bold text-white fs-8 mb-1">Kurs Efektywnego Działania 2.0</h4>
                        <p className="text-success fw-bold fs-5 mb-1">{installmentMode ? `4 × ${formatNumber(kedPrice)} zł` : `${formatNumber(kedPrice)} zł`}</p>
                        <p className="text-white-50 fs-11 mb-4">{installmentMode ? '4 wygodne raty · dostęp na całe życie' : 'raz · dostęp na całe życie'}</p>

                        <div className="d-flex flex-column gap-3 mb-4">
                            {(isParent ? parentKedItems : teenKedItems).map((item, i) => (
                                <div key={i} className="d-flex align-items-start gap-2">
                                    <i className="bi bi-check-square-fill text-success fs-11 mt-1 flex-shrink-0"></i>
                                    <span className="text-white fs-11">{item}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto pt-3"
                             style={{borderLeft: '3px solid rgba(42, 209, 103, 0.4)', paddingLeft: '12px'}}>
                            <p className="text-success fs-12 fst-italic fw-medium mb-0">To nie jest wydatek. To
                                inwestycja, która zwraca się już w pierwszych miesiącach.</p>
                        </div>
                    </motion.div>
                </div>

                {/* Card 3: Nie robię nic */}
                <div className="col-12 col-md-4">
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true}}
                        transition={{duration: 0.5, delay: 0.2}}
                        className="h-100 p-4 p-md-5 rounded-4 text-start"
                        style={glassCard}
                    >
                        <i className="bi bi-hourglass-split text-white-50 fs-4 d-block mb-3"></i>
                        <h4 className="fw-bold text-white fs-8 mb-1">Nie robię nic</h4>
                        <p className="text-white-50 fw-bold fs-5 mb-1">0 zł</p>
                        <p className="text-white-50 fs-11 mb-4">teraz · ale koszt rośnie każdego dnia</p>

                        <div className="d-flex flex-column gap-3 mb-4">
                            {(isParent ? parentNothingItems : teenNothingItems).map((item, i) => (
                                <div key={i} className="d-flex align-items-start gap-2">
                                    <i className="bi bi-circle text-white-50 fs-12 mt-1 flex-shrink-0"></i>
                                    <span className="text-white-50 fs-11">{item}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto pt-3"
                             style={{borderLeft: '3px solid rgba(255, 255, 255, 0.1)', paddingLeft: '12px'}}>
                            <p className="text-white-50 fs-12 fst-italic mb-0">
                                {isParent
                                    ? 'I gdzieś w środku ta cicha myśl: „mogłam coś zrobić — i nie zrobiłam."'
                                    : 'I gdzieś w środku ta cicha myśl: „mogłem/am coś zrobić — i nie zrobiłem/am."'
                                }
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <motion.p
                initial={{opacity: 0, y: 10}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.5}}
                className="fs-8 text-white fw-semibold mb-6"
            >
                {isParent ? 'Setki rodziców już podjęło tę decyzję.' : 'Setki nastolatków już podjęło tę decyzję.'}
            </motion.p>

            <AnimatedLink
                hashLink={true}
                to={ctaTarget}
                className='btn btn-lg btn-success fw-bold px-5 py-4 fs-9 mb-16'
            >
                <i className="bi bi-check-square-fill me-2"></i>
                {isParent
                    ? 'Chcę, żeby moje dziecko w końcu samo ogarniało naukę →'
                    : 'Chcę w końcu ogarnąć naukę na swoich zasadach →'
                }
            </AnimatedLink>
        </>
    );
}
